import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import PageMeta from "@/components/PageMeta";
import InstructorPilotWizard from "@/components/instructor/InstructorPilotWizard";
import {
  Plus,
  Users,
  Calendar,
  BarChart3,
  Settings,
  ArrowLeft,
  Moon,
  Rocket,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  ExternalLink,
  Video,
  MapPin,
  ChevronRight
} from "lucide-react";

const TEMPLATE_ICONS = {
  "tpl_data_dreaming": Moon,
  "tpl_emergent_host": Rocket
};

const TEMPLATE_COLORS = {
  "tpl_data_dreaming": { bg: "bg-violet-500", light: "bg-violet-50", text: "text-violet-600" },
  "tpl_emergent_host": { bg: "bg-blue-500", light: "bg-blue-50", text: "text-blue-600" }
};

export default function InstructorPilotsPage() {
  const navigate = useNavigate();
  const { profileId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  
  useEffect(() => {
    fetchProfiles();
  }, []);
  
  useEffect(() => {
    if (profileId) {
      fetchProfileDetails(profileId);
    }
  }, [profileId]);
  
  const fetchProfiles = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles || []);
      }
    } catch (err) {
      console.error("Failed to fetch profiles:", err);
      toast.error("Failed to load instructor pilots");
    } finally {
      setLoading(false);
    }
  };
  
  const fetchProfileDetails = async (id) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/profiles/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSelectedProfile(data.profile);
      }
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
  };
  
  const handleWizardComplete = (result) => {
    setShowWizard(false);
    fetchProfiles();
    if (result.profile) {
      navigate(`/admin/instructor-pilots/${result.profile.profile_id}`);
    }
  };
  
  const handleDeleteProfile = async (profileId) => {
    if (!confirm("Are you sure you want to delete this instructor pilot?")) return;
    
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/instructor-pilots/profiles/${profileId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Instructor pilot deleted");
        fetchProfiles();
        if (selectedProfile?.profile_id === profileId) {
          setSelectedProfile(null);
          navigate("/admin/instructor-pilots");
        }
      }
    } catch (err) {
      toast.error("Failed to delete pilot");
    }
  };
  
  if (showWizard) {
    return (
      <InstructorPilotWizard
        onComplete={handleWizardComplete}
        onCancel={() => setShowWizard(false)}
      />
    );
  }
  
  // Profile detail view
  if (profileId && selectedProfile) {
    const TemplateIcon = TEMPLATE_ICONS[selectedProfile.template_id] || Moon;
    const colors = TEMPLATE_COLORS[selectedProfile.template_id] || TEMPLATE_COLORS["tpl_data_dreaming"];
    
    return (
      <div className="min-h-screen bg-gray-50">
        <PageMeta title={`${selectedProfile.name} | Instructor Pilots`} />
        
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate("/admin/instructor-pilots")}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl ${colors.bg} flex items-center justify-center`}>
                  <TemplateIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{selectedProfile.name}</h1>
                  <p className="text-sm text-gray-500">
                    Host: {selectedProfile.host_name} • {selectedProfile.total_events} events
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => fetchProfileDetails(profileId)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button size="sm" className={colors.bg}>
                <Plus className="w-4 h-4 mr-2" />
                New Event
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <Card className={`${colors.light} border-0`}>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{selectedProfile.total_events}</p>
                    <p className="text-sm text-gray-600">Events</p>
                  </div>
                  <Calendar className={`w-8 h-8 ${colors.text} opacity-50`} />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{selectedProfile.total_rsvps || 0}</p>
                    <p className="text-sm text-gray-600">Total RSVPs</p>
                  </div>
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{selectedProfile.audience_types?.filter(a => a.enabled).length || 0}</p>
                    <p className="text-sm text-gray-600">Audience Types</p>
                  </div>
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-bold">{selectedProfile.interests?.filter(i => i.enabled).length || 0}</p>
                    <p className="text-sm text-gray-600">Interests</p>
                  </div>
                  <BarChart3 className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Tabs */}
          <Card>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto">
                <TabsTrigger value="overview" className="rounded-none border-b-2 border-transparent data-[state=active]:border-current px-4 py-3">
                  <Eye className="w-4 h-4 mr-2" />
                  Overview
                </TabsTrigger>
                <TabsTrigger value="events" className="rounded-none border-b-2 border-transparent data-[state=active]:border-current px-4 py-3">
                  <Calendar className="w-4 h-4 mr-2" />
                  Events
                </TabsTrigger>
                <TabsTrigger value="insights" className="rounded-none border-b-2 border-transparent data-[state=active]:border-current px-4 py-3">
                  <BarChart3 className="w-4 h-4 mr-2" />
                  Insights
                </TabsTrigger>
                <TabsTrigger value="settings" className="rounded-none border-b-2 border-transparent data-[state=active]:border-current px-4 py-3">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview" className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Host Info */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Host Information</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-gray-500">Name</p>
                        <p className="font-medium">{selectedProfile.host_name}</p>
                      </div>
                      {selectedProfile.host_title && (
                        <div>
                          <p className="text-sm text-gray-500">Title</p>
                          <p className="font-medium">{selectedProfile.host_title}</p>
                        </div>
                      )}
                      {selectedProfile.host_bio && (
                        <div>
                          <p className="text-sm text-gray-500">Bio</p>
                          <p className="text-gray-700">{selectedProfile.host_bio}</p>
                        </div>
                      )}
                      {selectedProfile.host_socials && (
                        <div className="flex gap-2 pt-2">
                          {selectedProfile.host_socials.instagram && (
                            <Badge variant="outline">{selectedProfile.host_socials.instagram}</Badge>
                          )}
                          {selectedProfile.host_socials.website && (
                            <Badge variant="outline">
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Website
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Configuration */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Configuration</h3>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Audience Types</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedProfile.audience_types?.filter(a => a.enabled).map(a => (
                            <Badge key={a.id} variant="secondary" className="text-xs">{a.label}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Interests</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedProfile.interests?.filter(i => i.enabled).map(i => (
                            <Badge key={i.id} variant="secondary" className="text-xs">{i.label}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 mb-2">Accessibility Options</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedProfile.accessibility_options?.filter(a => a.enabled).map(a => (
                            <Badge key={a.id} variant="secondary" className="text-xs">{a.label}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="events" className="p-6">
                {selectedProfile.events?.length > 0 ? (
                  <div className="space-y-3">
                    {selectedProfile.events.map(event => (
                      <Card key={event.event_id} className="hover:shadow-md transition-shadow cursor-pointer">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg ${event.event_type === "url" ? "bg-blue-100" : "bg-emerald-100"} flex items-center justify-center`}>
                                {event.event_type === "url" ? (
                                  <Video className="w-5 h-5 text-blue-600" />
                                ) : (
                                  <MapPin className="w-5 h-5 text-emerald-600" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{event.name}</p>
                                <div className="flex items-center gap-2 text-sm text-gray-500">
                                  <Badge variant={event.status === "published" ? "default" : "outline"} className="text-xs">
                                    {event.status}
                                  </Badge>
                                  {event.date && <span>{new Date(event.date).toLocaleDateString()}</span>}
                                  <span>{event.rsvp_count || 0} RSVPs</span>
                                </div>
                              </div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No events yet</p>
                    <Button className={colors.bg}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create First Event
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="insights" className="p-6">
                {selectedProfile.insights ? (
                  <div className="grid md:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Audience Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedProfile.insights.audience_breakdown?.length > 0 ? (
                          <div className="space-y-2">
                            {selectedProfile.insights.audience_breakdown.slice(0, 5).map(item => (
                              <div key={item.label} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">{item.label}</span>
                                <Badge variant="secondary">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No data yet</p>
                        )}
                      </CardContent>
                    </Card>
                    
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Interests Breakdown</CardTitle>
                      </CardHeader>
                      <CardContent>
                        {selectedProfile.insights.interests_breakdown?.length > 0 ? (
                          <div className="space-y-2">
                            {selectedProfile.insights.interests_breakdown.slice(0, 5).map(item => (
                              <div key={item.label} className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">{item.label}</span>
                                <Badge variant="secondary">{item.count}</Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No data yet</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Insights will appear after you have event RSVPs</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="settings" className="p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4">Profile Settings</h3>
                    <p className="text-sm text-gray-500">Profile ID: {selectedProfile.profile_id}</p>
                    <p className="text-sm text-gray-500">Template: {selectedProfile.template_id}</p>
                    <p className="text-sm text-gray-500">Created: {new Date(selectedProfile.created_at).toLocaleDateString()}</p>
                  </div>
                  
                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-red-600 mb-4">Danger Zone</h3>
                    <Button
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteProfile(selectedProfile.profile_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Instructor Pilot
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    );
  }
  
  // List view
  return (
    <div className="min-h-screen bg-gray-50">
      <PageMeta title="Instructor Pilots" description="Manage instructor host profiles" />
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Instructor Pilots</h1>
            <p className="text-gray-600">Host profiles for managing events with shared audience insights</p>
          </div>
          
          <Button onClick={() => setShowWizard(true)} data-testid="create-pilot-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Instructor Pilot
          </Button>
        </div>
        
        {/* Profiles Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : profiles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {profiles.map(profile => {
              const TemplateIcon = TEMPLATE_ICONS[profile.template_id] || Moon;
              const colors = TEMPLATE_COLORS[profile.template_id] || TEMPLATE_COLORS["tpl_data_dreaming"];
              
              return (
                <Card
                  key={profile.profile_id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/admin/instructor-pilots/${profile.profile_id}`)}
                  data-testid={`profile-card-${profile.profile_id}`}
                >
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 rounded-2xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                        <TemplateIcon className="w-7 h-7 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-lg text-gray-900 truncate">{profile.name}</h3>
                        <p className="text-sm text-gray-600">{profile.host_name}</p>
                        
                        <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {profile.total_events} events
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {profile.total_rsvps || 0} RSVPs
                          </span>
                        </div>
                        
                        {profile.org && (
                          <Badge variant="outline" className="mt-3 text-xs">
                            {profile.org.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Instructor Pilots Yet</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Create your first instructor pilot to start managing events with shared audience insights
              </p>
              <Button onClick={() => setShowWizard(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Instructor Pilot
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
